import type { Permission, Role } from '@gm-commercial/profile-model';

export enum RouteName {
  ADVISOR_PENDING_VEHICLES = 0,
  ASSET_LIST = 1,
  ASSET_DETAILS = 2,
  LOGIN = 3,
  USER_TERMS_AND_CONDITIONS = 4,
  ORGANIZATION_TERMS_AND_CONDITIONS = 5,
  ASSETS_REPORT = 6,
  SUPPORT = 7,
  BUSINESS_ENROLLMENT = 8,
  ALERTS = 9,
  INTEGRATION = 10,
  MRT_ASSET_LIST_REPORT = 11, // TODO: remove once MRT migration is complete
  ORG_ONBOARDING = 12,
  PRIVACY = 13,
  USER_TERMS = 14,
  EDIT_FLEET_CONFIGURATION = 15,
  STORE_VIEW = 16,
  ORDERS_VIEW = 17,
  DISPATCH = 18,
  WORK_PLAN_DETAILS = 19,
  CS_PLATFORM_ASSET_DETAILS = 20,
  CS_PLATFORM_ORDER_LIST = 21,
  CS_PLATFORM_ORDER_DETAILS = 22,
  CS_PLATFORM_SETTINGS = 23,
  CS_PLATFORM_NOTIFICATION_CENTER_LIST = 25,
  CS_PLATFORM_USERS_NOTIFICATIONS_SETTING = 26,
  CS_PLATFORM_ACCOUNTS_NOTIFICATIONS_SETTING = 27,
  ACCOUNT_PROFILE_PERSONAL = 28,
  ACCOUNT_PREFERENCES_LANGUAGE_REGION = 29,
  CS_PLATFORM_ACCOUNT_PREFERENCES = 30,
  CS_PLATFORM_ACCOUNT_PROFILE = 31,
  SUPPORT_CONTACT = 32,
  CS_PLATFORM_SUPPORT_CONTACT = 33,
  CS_PLATFORM_ACCOUNT_ORG_DETAILS = 34,
  CS_PLATFORM_ACCOUNT_FLEET_PROGRAMS = 35,
  CS_PLATFORM_PERIPHERALS_LIST = 36,
  CS_PLATFORM_DRIVERS_LIST = 37,
  CS_PLATFORM_USER_LIST = 38,
  CS_PLATFORM_PENDING_VEHICLES = 39,
  ADVISOR_VEHICLE_LIST = 40,
  PLANS_LIST_VIEW = 42,
  PLANS_DETAIL_VIEW = 43,
  CS_PLATFORM_SAFETY_AND_SECURITY_NOTIFICATIONS_SETTINGS = 44,
  FAQS = 46,
  LEGAL = 47,
  ORG_VEHICLES = 48,
  MFA_POC = 49,
  PLAN_ORDER_SUMMARY = 50,
  ORDER_CONFIRMATION = 51,
  SHOP = 52,
  ORGANIZATIONS_HOME = 53,
  ORGANIZATIONS_VEHICLES = 54,
  ORGANIZATIONS_MAP = 55,
  ORGANIZATIONS_VEHICLES_MAP = 56,
  ORGANIZATIONS = 57,
  ORGANIZATIONS_DETAILS = 58,
  ORGANIZATIONS_FLEETS = 59,
  ORGANIZATIONS_FLEETS_DETAILS = 60,
  ORGANIZATIONS_FLEETS_DETAILS_EDIT_CONFIGS = 61,
  ORGANIZATIONS_USERS = 64,
  ORGANIZATIONS_USERS_DETAILS = 65,
  CS_PLATFORM_DRIVERS_DETAILS = 66,
  PLAN_DETAIL_VIEW = 67,
  ORGANIZATIONS_VEHICLES_BATCH = 69,
  ORGANIZATIONS_MAP_VEHICLE_DETAILS_OVERVIEW = 70,
  ORGANIZATIONS_MAP_VEHICLE_DETAILS_TRIPS = 71,
  ORGANIZATIONS_SAFETY = 72,
  ORGANIZATIONS_SAFETY_EVENT_DETAIL = 73,
  ORGANIZATIONS_REPORTS = 74,
  ORGANIZATIONS_MAP_GEOFENCES = 75,
  ORGANIZATIONS_MAP_GEOFENCES_CREATE = 76,
  ORGANIZATIONS_MAP_GEOFENCES_DETAILS = 77,
  ORGANIZATIONS_MAP_GEOFENCES_EDIT = 78,
  ORGANIZATIONS_MAP_CHARGING_STATIONS = 79,
  ORGANIZATIONS_SAFETY_DRIVER_PERFORMANCE = 80,
  CS_PLATFORM_DRIVERS_DETAILS_CHARGE_SESSION = 81,
  ORGANIZATIONS_INSIGHTS = 82,
  ORGANIZATIONS_SAFETY_EVENT_HISTORY = 83,
  ORGANIZATIONS_SAFETY_LEADERBOARD = 84,
  ORGANIZATIONS_VEHICLES_DETAILS = 85,
  ORGANIZATIONS_VEHICLES_DETAILS_LOCATION = 86,
  ORGANIZATIONS_VEHICLES_DETAILS_TRIPS = 87,
  ORGANIZATIONS_VEHICLE_BOARDING_HISTORY = 88,
  ORGANIZATIONS_CUSTOM_VIDEO_REQUEST = 89,
  CS_PLATFORM_FEEDBACK = 90,
  ORGANIZATIONS_CUSTOM_VIDEO_DETAILS = 91,
  BPS_VEHICLE_ENROLLMENT = 92,
  SHOP_ADDONS = 93,
  SHOP_PLANS = 94,
  SHOP_ADDON_ORDER = 95,
  SHOP_ADDON_ORDER_CONFIRMATION = 97,
  GETTING_STARTED = 98,
  CS_PLATFORM_SETTINGS_VIEW = 99,
  ONSTAR_PLATFORM_IS_READY = 100,
  MIGRATION_NOT_COMPLETE = 101,
  TERMS_NOT_SIGNED = 102,
  ORGANIZATIONS_EXPENSES = 103,
  ORGANIZATIONS_SHOP_INTEGRATIONS = 104,
  ACCOUNT_ORG_INTEGRATIONS = 105,
  ACCOUNT_ORG_INTEGRATIONS_PARTNER = 106,
  CS_PLATFORM_ACCOUNT_NOTIFICATION_DETAIL = 107,
  CS_PLATFORM_SAFETY_AND_SECURITY_NOTIFICATION_DETAIL = 108,
  CS_PLATFORM_VEHICLE_NOTIFICATION_DETAIL = 109,
  CS_PLATFORM_DIAGNOSTICS_NOTIFICATION_NEW = 110,
  CS_PLATFORM_DIAGNOSTICS_NOTIFICATION_DETAIL = 111,
  CS_PLATFORM_GEOFENCE_NOTIFICATION_DETAIL = 112,
}

export type RouteConfig = {
  name: RouteName;
  authorizedPerms: Permission[];
  path: string;
  element: React.LazyExoticComponent<() => JSX.Element>;
  authorizedRoles?: Role[];
  excludedRoles?: Role[];
  requiredFeatureFlags?: string[];
  /** Route/path is enabled when ANY of these are on, unlike `requiredFeatureFlags` (all must be on). */
  anyFeatureFlags?: string[];
  requiresContextMatch?: boolean;
  customTemplate?: React.FC<{ children: React.ReactNode }>;
};

export const AppRoutePaths = {
  ROOT: '/',
  PAGE_NOT_FOUND: '/page-not-found',
  UNAUTHORIZED: '/unauthorized',
  SERVICE_UNAVAILABLE: '/service-unavailable',
  BUSINESS_ENROLLMENT: '/business-enrollment',
  LOGOUT: '/logout',
  LOGIN: '/login',
  MOBILE_APP_REQUIRED: '/mobile-app-required',
  PRIVACY: '/privacy-statement',
  TERMS_CONDITIONS: '/user-terms',
  CANCELLED_INVITE: '/invite-cancelled',
  EXPIRED_INVITE: '/invite-expired',
  CS_BACKOFFICE: '/backoffice',
  QUICK_PAY: '/quickpay',
  QUICK_PAY_PAYMENT: '/quickpay/payment',
  QUICK_PAY_CONFIRMATION: '/quickpay/confirmation',
} as const;

export const SSO_ROUTES = [AppRoutePaths.LOGIN.valueOf()];
